/**
 * Hopelapp Brand Color Palette
 * Palette officielle de la charte graphique Hopelapp
 *
 * Usage:
 * import { hopelappPalette, hopelappDarkPalette } from "assets/theme/hopelapp-palette";
 */

// ==========================================
// PALETTE HOPELAPP - LIGHT MODE
// ==========================================

export const hopelappPalette = {
  // Couleurs principales de la charte
  brand: {
    // Bleu-vert principal (couleur signature)
    teal: {
      main: "#357f84",
      light: "#4a9499",
      dark: "#2a6569",
      contrastText: "#ffffff",
    },
    // Jaune doré (accent)
    gold: {
      main: "#e3b025",
      light: "#eac04d",
      dark: "#b88d1d",
      contrastText: "#162117",
    },
    // Vert menthe clair
    mint: {
      main: "#cde4dc",
      light: "#e0efe9",
      dark: "#a8c9bd",
      contrastText: "#162117",
    },
    // Rose pêche clair
    peach: {
      main: "#f4d8ce",
      light: "#f9e8e1",
      dark: "#e5bfb0",
      contrastText: "#162117",
    },
    // Corail/Saumon
    coral: {
      main: "#f49b7c",
      light: "#f7b59d",
      dark: "#e07a58",
      contrastText: "#162117",
    },
    // Vert foncé (textes)
    darkGreen: {
      main: "#162117",
      light: "#2c3c2e",
      dark: "#0d140e",
      contrastText: "#ffffff",
    },
  },

  // Couleurs sémantiques mappées sur la palette Hopelapp
  semantic: {
    primary: "#357f84", // Teal
    secondary: "#7b809a", // Gris neutre
    info: "#357f84", // Teal (cohérence avec primary)
    success: "#4CAF50", // Vert standard
    warning: "#e3b025", // Gold Hopelapp
    error: "#f49b7c", // Coral Hopelapp (plus doux que rouge vif)
    light: "#cde4dc", // Mint
    dark: "#162117", // Dark green
  },

  // Backgrounds
  background: {
    default: "#f5f7f6", // Légèrement teinté de vert
    paper: "#ffffff",
    card: "#ffffff",
    sidebar: "#357f84", // Teal pour la sidebar
    hover: "#cde4dc", // Mint pour hover
  },

  // Textes
  text: {
    primary: "#162117", // Dark green
    secondary: "#5a6b5c", // Vert gris
    disabled: "#9ca89e",
    hint: "#7b8a7d",
  },

  // Bordures
  border: {
    light: "#cde4dc",
    main: "#a8c9bd",
    dark: "#357f84",
  },

  // États interactifs
  action: {
    active: "#357f84",
    hover: "rgba(53, 127, 132, 0.08)",
    selected: "rgba(53, 127, 132, 0.14)",
    disabled: "rgba(22, 33, 23, 0.26)",
    focus: "rgba(53, 127, 132, 0.12)",
  },
} as const;

// ==========================================
// PALETTE HOPELAPP - DARK MODE (BLACK)
// ==========================================

export const hopelappDarkPalette = {
  // Couleurs principales adaptées au mode sombre
  brand: {
    teal: {
      main: "#4a9fa5", // Plus clair pour contraste
      light: "#6bb5ba",
      dark: "#357f84",
      contrastText: "#0d1410",
    },
    gold: {
      main: "#eac04d", // Plus clair
      light: "#f0d070",
      dark: "#e3b025",
      contrastText: "#0d1410",
    },
    mint: {
      main: "#3d5a50", // Adapté au dark
      light: "#4d6b60",
      dark: "#2d4a40",
      contrastText: "#e8f0ec",
    },
    peach: {
      main: "#e5bfb0", // Légèrement assombri
      light: "#f4d8ce",
      dark: "#c9a090",
      contrastText: "#0d1410",
    },
    coral: {
      main: "#f7b59d", // Plus clair pour contraste
      light: "#fac9b5",
      dark: "#f49b7c",
      contrastText: "#0d1410",
    },
    darkGreen: {
      main: "#e8f0ec", // Inversé pour le texte
      light: "#ffffff",
      dark: "#d0e0d8",
      contrastText: "#0d1410",
    },
  },

  // Couleurs sémantiques pour dark mode
  semantic: {
    primary: "#4a9fa5", // Teal plus clair
    secondary: "#8f93a9",
    info: "#4a9fa5",
    success: "#67bb6a",
    warning: "#eac04d", // Gold plus clair
    error: "#f7b59d", // Coral plus clair
    light: "#2d3d35",
    dark: "#e8f0ec",
  },

  // Backgrounds dark
  background: {
    default: "#0d1410", // Noir teinté de vert
    paper: "#141c18",
    card: "#1a2420",
    sidebar: "#357f84", // Vert Hopelapp - même couleur qu'en mode light
    hover: "#1f2d28",
  },

  // Textes dark
  text: {
    primary: "#e8f0ec",
    secondary: "#a8b8ac",
    disabled: "#5a6b5c",
    hint: "#7b8a7d",
  },

  // Bordures dark
  border: {
    light: "#2d3d35",
    main: "#3d5a50",
    dark: "#4a9fa5",
  },

  // États interactifs dark
  action: {
    active: "#4a9fa5",
    hover: "rgba(74, 159, 165, 0.12)",
    selected: "rgba(74, 159, 165, 0.20)",
    disabled: "rgba(232, 240, 236, 0.26)",
    focus: "rgba(74, 159, 165, 0.16)",
  },
} as const;

// ==========================================
// TYPES
// ==========================================

export type HopelappPalette = typeof hopelappPalette;
export type HopelappDarkPalette = typeof hopelappDarkPalette;
export type HopelappBrandColor = keyof typeof hopelappPalette.brand;

// ==========================================
// HELPERS
// ==========================================

/**
 * Récupère une couleur de la palette en fonction du mode
 */
export const getHopelappColor = (
  colorKey: HopelappBrandColor,
  variant: "main" | "light" | "dark" = "main",
  isDarkMode = false
): string => {
  const palette = isDarkMode ? hopelappDarkPalette : hopelappPalette;
  return palette.brand[colorKey][variant];
};

/**
 * Liste des couleurs pour les sélecteurs (ex: sidebar color)
 */
export const hopelappColorOptions = [
  { key: "teal", label: "Teal", color: "#357f84" },
  { key: "gold", label: "Gold", color: "#e3b025" },
  { key: "mint", label: "Mint", color: "#cde4dc" },
  { key: "coral", label: "Coral", color: "#f49b7c" },
  { key: "darkGreen", label: "Dark Green", color: "#162117" },
] as const;

export default hopelappPalette;
